import Alert from '@/common/components/Alert'
import Button from '@/common/components/Button'
import Checkbox from '@/common/components/Checkbox'
import { CurrencyTextField } from '@/common/components/CurrencyTextField'
import DragHandle from '@/common/components/DragHandle'
import { ErrorMessage } from '@/common/components/ErrorMessage'
import Modal from '@/common/components/Modal'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import Select from '@/common/components/Select'
import SelectController from '@/common/components/SelectController'
import { UnitTextField } from '@/common/components/UnitTextField'
import useDebounce from '@/common/hooks/useDebounce'
import { useDragAndDrop } from '@/common/hooks/useDragAndDrop'
import { Mapper } from '@/common/services/mapper'
import { Option } from '@/common/types/option'
import { cn } from '@/common/utils/cn'
import { formatCurrency, formatPercentage } from '@/common/utils/formatters'
import { FormTransformers } from '@/common/utils/formTransformers'
import { CompanyData } from '@/features/companies/types/companies.type'
import { useEstablishmentCompanies } from '@/features/establishments/hooks/useEstablishmentCompanies'
import { EstablishmentCompanyTransformer } from '@/features/establishments/services/establishmentCompanyTransformer'
import { useFeeTemplates } from '@/features/feeTemplates/hooks/useFeeTemplates'
import { FeeTemplateData } from '@/features/feeTemplates/types/feeTemplates.type'
import { useUpdateFinancial } from '@/features/privateCompanies/hooks/politics/useUpdateFinancial'
import { billIntegrationProcessOptions } from '@/features/privateCompanies/pages/Politics/Show/constants/billIntegrationProcess.constant'
import {
  UpdateFinancialSchema,
  updateFinancialSchema,
} from '@/features/privateCompanies/schemas/politics/updateFinancial.schema'
import { CompanyPoliticsFinancialTransformer } from '@/features/privateCompanies/services/politics/companyPoliticsFinancialTransformer'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { AlertCircleIcon, XIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Controller, SubmitHandler, useFieldArray, useForm } from 'react-hook-form'

interface UpdateFinancialModalProps {
  company?: CompanyData
}

export const UpdateFinancialModal = ({ company }: UpdateFinancialModalProps) => {
  const queryClient = useQueryClient()

  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const [searchEstablishment, setSearchEstablishment] = useDebounce('')
  const [searchFeeTemplate, setSearchFeeTemplate] = useDebounce('')

  const { data: establishmentOptions, isPending: establishmentIsPending } = useEstablishmentCompanies({
    filters: { search: searchEstablishment, caModuleEnabled: true },
    mapper: Mapper.mapWithTransformer({
      transformer: EstablishmentCompanyTransformer.optionTransformer({
        valueFieldName: 'id',
        addAllEstablishmentOption: true,
      }),
    }),
  })

  const { data: feeTemplateOptions } = useFeeTemplates({
    filters: { search: searchFeeTemplate, category: 'establishment' },
    mapper: Mapper.mapToOptionsWithItem<FeeTemplateData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const formMethods = useForm<UpdateFinancialSchema>({
    resolver: zodResolver(updateFinancialSchema),
  })

  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    reset,
    watch,
    formState: { errors },
  } = formMethods

  const {
    fields: specialEntriesField,
    append: appendSpecialEntry,
    remove: removeSpecialEntry,
    update: updateSpecialEntry,
    move: moveSpecialEntry,
  } = useFieldArray({
    control,
    name: 'company.fee_rule.special_entries',
    keyName: '_id',
  })

  const { getItemProps, getHandleProps, draggedIndex, dragOverIndex } = useDragAndDrop({
    onReorder: (fromIndex, toIndex) => moveSpecialEntry(fromIndex, toIndex),
    dragImageClassName: '!bg-yellow-op-2',
  })

  const shouldScrollToNewEntryRef = useRef(false)

  useEffect(() => {
    if (!shouldScrollToNewEntryRef.current) return

    shouldScrollToNewEntryRef.current = false

    const newItem = document.querySelector(`[data-drag-item-index="${specialEntriesField.length - 1}"]`)

    newItem?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [specialEntriesField?.length])

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['company', company?.id] })
    setIsOpen(false)
  }

  const { mutate: updateFinancial, isPending: updateFinancialIsPending } = useUpdateFinancial({
    company,
    onSuccess,
    onError: FormTransformers.errorsTransformer<UpdateFinancialSchema>({ setError, setMessages, transformer: CompanyPoliticsFinancialTransformer.errorsTransformer() }),
  })

  const onSubmit: SubmitHandler<UpdateFinancialSchema> = () => {
    let hasError = false;

    watch('company.fee_rule.special_entries')?.forEach((entry, index) => {
      if (!entry?.fee_template) {
        setError(`company.fee_rule.special_entries.${index}.fee_template`, { message: 'Campo obrigatório' })
        hasError = true
      }

      if (!entry?.value) {
        setError(`company.fee_rule.special_entries.${index}.value`, { message: 'Campo obrigatório' })
        hasError = true
      }
    })

    if (hasError)
      return;

    updateFinancial(watch())
  }

  useEffect(() => {
    const sortedSpecialEntries = [...(company?.fee_rule?.special_entries || [])]
      .sort((a, b) => a?.execution_priority - b?.execution_priority)

    reset({
      company: {
        bill_integration_process: billIntegrationProcessOptions.find(
          (option) => option.value === company?.bill_integration_process,
        ),
        fee_rule: {
          special_entries: sortedSpecialEntries.map((specialEntry) => ({
            id: specialEntry?.id,
            fee_template: {
              id: specialEntry?.fee_template?.id,
              display_name: specialEntry?.fee_template?.display_name,
              calculate_as: specialEntry?.fee_template?.calculate_as,
            },
            target: specialEntry?.target ? {
              value: specialEntry?.target?.id,
              label: `${specialEntry?.target?.general_information?.fantasy_name || ''} (${specialEntry?.target?.general_information?.official_name || ''})`,
            } : {
              value: 'all',
              label: 'Todos os Estabelecimentos',
            },
            value: specialEntry?.fee_template?.calculate_as === 'fixed'
              ? formatCurrency(Number(specialEntry?.value || 0))
              : formatPercentage(Number(specialEntry?.value || 0)),
            not_use_if_has_special_fee: specialEntry?.not_use_if_has_special_fee ?? false,
            execution_priority: specialEntry?.execution_priority,
          }))
        }
      },
    })
  }, [isOpen])

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onConfirm={handleSubmit(onSubmit)}
      Trigger={
        <Button
          name="open_update_fee_details_modal"
          operationKlassToCheckPermission="Admin::Client::Companies::FeeRuleUpdate"
          className="h-[2.125rem] text-sm text-white-800"
        >
          Editar
        </Button>
      }
      title="Editando Tarifas"
      dataTestid="update_fee_details"
      useCancellableFooter
      className="max-w-[calc(100vw-4rem)]"
      confirmButtonClassName="bg-blue-700 rounded-br-lg"
      cancelButtonClassName="bg-white-100 rounded-bl-lg"
      bodyClassName='max-h-[calc(100vh-12rem)] overflow-auto'
      disabled={updateFinancialIsPending}
      confirmButtonIsLoading={updateFinancialIsPending}
      body={
        <>
          <form className="flex flex-col gap-y-4 text-white-800">
            <SelectController
              control={control}
              name="company.bill_integration_process"
              label="Enviar fatura para processamento"
              errorMessage={errors?.company?.bill_integration_process?.message}
              options={billIntegrationProcessOptions}
              isDisabled={updateFinancialIsPending}
              markAsRequired
            />

            <div className="flex flex-col gap-y-2">
              <p className="text-[16px]">Tarifas especiais:</p>

              <Alert variant="warning" Icon={AlertCircleIcon} containerClassName="bg-transparent p-0">
                <div className="flex flex-col">
                  <p className="text-sm">
                    Defina a ordem da aplicação, a mais acima será a tarifa qual será aplicada primeiro.
                  </p>

                  <p className="text-xs italic font-light">
                    Caso a próxima também se aplique ao posto, e esteja configurada para não aplicar quando houver uma
                    outra especial, ela não será aplicada.
                  </p>
                </div>
              </Alert>
            </div>

            <Select
              placeholder="Pesquisar Estabelecimentos"
              value={null}
              onInputChange={setSearchEstablishment}
              onChange={(newValue: any) => {
                shouldScrollToNewEntryRef.current = true

                appendSpecialEntry({
                  id: '',
                  target: {
                    value: (newValue as Option).value,
                    label: (newValue as Option).label,
                  },
                  value: '',
                  not_use_if_has_special_fee: false,
                })
              }}
              options={establishmentOptions || []}
              isDisabled={updateFinancialIsPending}
              isLoading={establishmentIsPending}
            />

            {specialEntriesField?.map((specialEntry, index) => (
              <div
                key={specialEntry._id}
                {...getItemProps(index)}
                className={cn(
                  'flex gap-2 bg-white-200 items-start rounded-md border-2 border-transparent transition-colors p-2',
                  draggedIndex === index && 'opacity-50 bg-yellow-op-2',
                  dragOverIndex === index && draggedIndex !== index && 'border-blue-700 border-dashed',
                )}
              >
                <div className="flex items-center gap-1 mt-2">
                  <DragHandle
                    {...getHandleProps(index)}
                    disabled={updateFinancialIsPending}
                    data-testid={`drag_handle_special_entry_${index}`}
                  />

                  <span className="text-sm text-white-800 min-w-[1ch] text-center tabular-nums">
                    {index}
                  </span>
                </div>

                <div className="flex-1 space-y-4">
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline-error"
                      className="size-[54px] mt-[18px]"
                      onClick={() => removeSpecialEntry(index)}
                      disabled={updateFinancialIsPending}
                    >
                      <XIcon />
                    </Button>

                    <Select
                      label="Estabelecimento"
                      className="w-full"
                      onInputChange={setSearchEstablishment}
                      value={specialEntry.target}
                      isDisabled
                    />

                    <div className='w-full'>
                      <Select
                        label="Tarifa"
                        className="w-full"
                        onInputChange={setSearchFeeTemplate}
                        value={
                          specialEntry.fee_template
                            ? { value: specialEntry.fee_template.id, label: specialEntry.fee_template.display_name }
                            : null
                        }
                        onChange={(newValue: any) => {
                          const feeTemplate = (newValue.item as FeeTemplateData);

                          updateSpecialEntry(index, {
                            ...specialEntry,
                            fee_template: {
                              id: feeTemplate.id,
                              display_name: feeTemplate.display_name,
                              calculate_as: feeTemplate.calculate_as,
                            },
                            value: feeTemplate.default_value
                              ? feeTemplate.calculate_as === 'fixed'
                                ? formatCurrency(Number(feeTemplate.default_value))
                                : formatPercentage(Number(feeTemplate.default_value))
                              : '',
                          })

                          clearErrors(`company.fee_rule.special_entries.${index}.fee_template`)
                          clearErrors(`company.fee_rule.special_entries.${index}.value`)
                        }}
                        options={feeTemplateOptions.filter((feeTemplate) => {
                          return !watch('company.fee_rule.special_entries')?.filter((entry) => {
                            return entry?.target?.value === specialEntry.target?.value
                          })?.some((entry) => entry?.fee_template?.id === feeTemplate.value)
                        }) || []}
                        isDisabled={!!specialEntry.fee_template || updateFinancialIsPending}
                        markAsRequired={!specialEntry.id}
                      />

                      {errors.company?.fee_rule?.special_entries?.[index]?.fee_template?.message
                        ? (
                          <ErrorMessage
                            name={`company.fee_rule.special_entries.${index}.fee_template`}
                            message={errors.company?.fee_rule?.special_entries?.[index]?.fee_template?.message}
                          />
                        )
                        : null}
                    </div>

                    {specialEntry.fee_template && (
                      <Controller
                        control={control}
                        name={`company.fee_rule.special_entries.${index}.value`}
                        disabled={updateFinancialIsPending}
                        render={({ field }) => specialEntry.fee_template?.calculate_as === 'fixed' ? (
                          <CurrencyTextField
                            label="Valor"
                            className="w-1/2"
                            allowNegative
                            {...field}
                            errorMessage={errors?.company?.fee_rule?.special_entries?.[index]?.value?.message || undefined}
                            markAsRequired
                          />
                        ) : (
                          <UnitTextField
                            unit="%"
                            decimals={2}
                            minValue={-100}
                            maxValue={100}
                            allowNegative
                            label="Valor"
                            className="w-1/2"
                            {...field}
                            errorMessage={errors?.company?.fee_rule?.special_entries?.[index]?.value?.message || undefined}
                            markAsRequired
                          />
                        )}
                      />
                    )}
                  </div>

                  <Controller
                    control={control}
                    name={`company.fee_rule.special_entries.${index}.not_use_if_has_special_fee`}
                    render={({ field: { value, onChange, name, ref } }) => (
                      <Checkbox
                        label="Ignorar se houver uma tarifa acima já aplicada"
                        checked={!!value}
                        onChange={(event) => onChange((event.target as HTMLInputElement).checked)}
                        name={name}
                        ref={ref}
                        disabled={updateFinancialIsPending}
                      />
                    )}
                  />
                </div>
              </div>
            ))}
          </form>

          {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
        </>
      }
    />
  )
}
